import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import SystemService from '../../../data/system-service';
import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    labelMargin: {
        marginRight: theme.spacing(5),
    },
    formMargin: {
        marginRight: theme.spacing(5),
    },
    darkBG: {
        background: theme.palette.primary.dark,
    },
    mailLog: {
        overflow: 'scroll',
        maxHeight: 300,
        background: theme.styled.paper.background,
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        '& li': {
            padding: '2px 0px 2px 15px',
        },
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
}));

const Mail = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [mailLog, setMailLog] = useState([]);
    const [original, setOriginal] = useState({});
    const [email, setEmail] = useState({
        exim_type: 'internet',
        auth_pass: '',
        auth_user: '',
        from_mail: '',
        from_name: '',
        host: '',
    });
    const [changes, setChanges] = useState(false);
    const mailForm = useValidate({});
    const [disableCheck, setDisableCheck] = useState(false);
    const [init, setInit] = useState(true);
    const mailPerms = Auth.permissions.get('Mail');

    useEffect(() => {
        SystemService.mailLogs().then(res => {
            setMailLog(res);
        });

        setInit(false);
    }, []);

    useEffect(() => {
        if (systemSettings && systemSettings.email) {
            const e = _cloneDeep(systemSettings.email);
            setEmail(e);
            setOriginal(_cloneDeep(systemSettings.email));
        }
    }, [systemSettings]);

    useEffect(() => {
        if (email.exim_type === 'internet') {
            mailForm.set({});
        }
        setChanges(!_isEqual(email, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email]);

    const handleAuthUser = (e) => {
        setEmail({ ...email, auth_user: e.currentTarget.value });
    };

    const handleAuthPass = (e) => {
        setEmail({ ...email, auth_pass: e.currentTarget.value });
    };

    const handleFromMail = (e) => {
        setEmail({ ...email, from_mail: e.currentTarget.value });
    };

    const handleFromName = (e) => {
        setEmail({ ...email, from_name: e.currentTarget.value });
    };

    const handleSmartHost = (e, validID, valid) => {
        mailForm.set({ ...mailForm.get(), [validID]: valid });

        setEmail({ ...email, host: e.target.value });
    };

    const handleUpdateEximType = (eximType) => {
        const e = { ...email };
        e.exim_type = eximType;
        setEmail(e);
    };

    const handleCheckHost = () => {
        setDisableCheck(true);
        SystemService.checkMailHost(email).then(res => {
            toast.success('Check successful');
            setDisableCheck(false);
        }, err => {
            toast.error(err.error);
            setDisableCheck(false);
        });
    };

    const handleSave = () => {
        const e = { ...email };
        if (email.exim_type === 'internet') {
            e.host = '';
        }
        updateSystemSettings({ email: e });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    return (
        <Paper>
            <Box p={5}>
                <Grid container spacing={5}>
                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="h6" className={classes.labelMargin}>Authentication</Typography>
                        </Box>
                        <Box>
                            <TextField
                                disabled={!mailPerms.write}
                                aria-label="username"
                                label="Username"
                                fullWidth
                                value={email.auth_user}
                                onChange={handleAuthUser}
                            />
                        </Box>
                        <Box mt={2}>
                            <TextField
                                disabled={!mailPerms.write}
                                aria-label="password"
                                label="Password"
                                type="password"
                                fullWidth
                                value={email.auth_pass}
                                onChange={handleAuthPass}
                            />
                            <FormHelperText>Mail server username and password. These values may be empty.</FormHelperText>
                        </Box>
                        <Box mt={4}>
                            <Typography variant="h6" className={classes.labelMargin}>Send As</Typography>
                        </Box>
                        <Box>
                            <TextField
                                disabled={!mailPerms.write}
                                aria-label="from address"
                                label="From Address"
                                fullWidth
                                value={email.from_mail}
                                onChange={handleFromMail}
                            />
                        </Box>
                        <Box mt={2}>
                            <TextField
                                disabled={!mailPerms.write}
                                aria-label="from display name"
                                label="From Display Name"
                                fullWidth
                                value={email.from_name}
                                onChange={handleFromName}
                            />
                            <FormHelperText>Mail adddress and display name used when sending mail.</FormHelperText>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="h6" className={classes.labelMargin}>Delivery Type</Typography>
                        </Box>
                        <Box mt={2}>
                            <FormControl>
                                <ButtonGroup disabled={!mailPerms.write} color="primary" variant="contained">
                                    <Button className={email.exim_type === 'internet' ? classes.darkBG : ''} aria-label="direct smtp" onClick={() => { handleUpdateEximType('internet') }}>Direct SMTP</Button>
                                    <Button className={email.exim_type === 'smarthost' ? classes.darkBG : ''} aria-label="smarthost" onClick={() => { handleUpdateEximType('smarthost') }}>Smarthost</Button>
                                </ButtonGroup>
                            </FormControl>
                        </Box>
                        {email.exim_type === 'smarthost' && <Box mt={3}>
                            <Validator
                                valueType="host"
                                errorText="Hostname is invalid"
                                disabled={!mailPerms.write}
                                placeholder={'smtp.example.com:587'}
                                fullWidth
                                label=""
                                kick={init}
                                value={email.host}
                                onChange={handleSmartHost}
                                endAdornment={mailPerms.write ? <Button disabled={!checkValidity(mailForm.get()) || disableCheck} aria-label="check" onClick={handleCheckHost}>Check</Button> : null}
                            />
                        </Box>}
                        <Box mt={3}>
                            <Box mt={3}>
                                <List className={classes.mailLog}>
                                    {mailLog.map((v, i) => {
                                        return <ListItem key={'mail-log-' + i}>{v}</ListItem>;
                                    })}
                                </List>
                            </Box>
                        </Box>
                    </Grid>
                    {mailPerms.write && <Grid item xs={12}>
                        <Button
                            className={classes.buttonRight}
                            variant="contained"
                            aria-label="save"
                            color="primary"
                            onClick={handleSave}
                            disabled={!(email.exim_type === 'smarthost' ? checkValidity(mailForm.get()) : true)}
                        >
                            Save
                        </Button>
                        <Button className={classes.buttonRight} variant="contained" aria-label="cancel" color="secondary" onClick={handleCancel}>Cancel</Button>
                    </Grid>}
                </Grid>
            </Box>
        </Paper>
    );
};

export default Mail;
